import { useState, useCallback, useMemo, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useHistory } from 'react-router-dom';
import Auth from '../../services/auth';
import Utils from '../../utils/utils';
import Icon from "../controls/Icon";
import ChangeDomain from '../domain/ChangeDomain';
import { useTranslation } from "react-i18next";
import UserSettings from '../user/UserSettings';
import DeleteDomain from '../domain/DeleteDomain';
import { useGetDomain, useGetUser } from '../../common/Hooks';
import PubSub from '../../services/PubSub';
import AboutModal from '../AboutModal';
import ResetPassword from '../user/ResetPassword';

const Controls = () => {
    const [showChangeDomainModal, setShowChangeDomainModal] = useState(false);
    const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [showDeleteDomainModal, setShowDeleteDomainModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [hasUnsavedChanges, setHasUnSavedChanges] = useState(0);
    const [translate] = useTranslation();
    const [domain] = useGetDomain();
    const [user] = useGetUser();

    const history = useHistory();

    const handleLogout = useCallback(async () => {
        await Auth.logout();
        history.push("/auth");
    }, [history]);

    useEffect(() => {
        const unsavedChangesSubId = PubSub.subscribe("has-unsaved-changes", () => {
            setHasUnSavedChanges((prev) => prev + 1);
        });
        const nounsavedChangesSubId = PubSub.subscribe("no-unsaved-changes", () => {
            setHasUnSavedChanges((prev) => prev === 0 ? 0 : prev - 1);
        });

        return () => {
            PubSub.unsubscribe("has-unsaved-changes", unsavedChangesSubId);
            PubSub.unsubscribe("no-unsaved-changes", nounsavedChangesSubId);
        };
    }, []);

    const handleChangeDomainModalClick = useCallback(() => {
        if (hasUnsavedChanges === 0) {
            setShowChangeDomainModal(true);
        } else {
            PubSub.publish("show_info", "OTM_DOMAIN_UNSAVED_CHANGES_WARNING", "OTM_WARNING");
        }
    }, [hasUnsavedChanges]);

    const getUserDisplayName = (user) => {
        if (user.firstName && user.lastName) {
            return user.firstName + " " + user.lastName;
        }

        return user.email;
    }

    const getDropdownMenu = useMemo(() => {
        if (user.appAdmin) {
            return (
                <>
                    <Dropdown.Item onClick={handleChangeDomainModalClick}>{translate("OTM_SWITCH_DOMAINS")}</Dropdown.Item>
                    {Utils.isRootDomain() && <Dropdown.Item onClick={() => setShowDeleteDomainModal(true)}>{translate("OTM_DELETE_DOMAIN")}</Dropdown.Item>}
                </>
            )
        }
        if (!user.singleDomainUser) {
            return <Dropdown.Item onClick={handleChangeDomainModalClick}>{translate("OTM_SWITCH_DOMAINS")}</Dropdown.Item>
        }
        return <></>
    }, [user, handleChangeDomainModalClick, translate]);

    return (
        <>
            <div className="collapse navbar-collapse">
                <div className="navbar-text mx-auto h4 p-0 text-white">{`${translate("OTM_CONFIGURE")} - ${domain.name}`}</div>
                <div className="navbar-nav">
                    <button className="btn nav-link border-0 ms-2 p-0 bg-transparent" onClick={() => window.open(process.env.REACT_APP_OWN_WEBSITE)}>
                        <Icon icon="Language" className="d-block" />
                    </button>

                    <button className="btn nav-link border-0 ms-2 p-0 bg-transparent" onClick={() => window.open(domain.helpUrl || process.env.REACT_APP_DEFAULT_HELP_URL)}>
                        <Icon icon="Help" className="d-block" />
                    </button>

                    <Dropdown>
                        <Dropdown.Toggle bsPrefix="nav-link" className="ms-2 p-0 border-0 bg-transparent">
                            <Icon icon="AccountCircle" className="d-block" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Header>{getUserDisplayName(user)}</Dropdown.Header>
                            <Dropdown.Divider />
                            {getDropdownMenu}

                            <Dropdown.Item onClick={() => setShowUserSettingsModal(true)}>{translate("OTM_MY_SETTINGS")}</Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => {
                                    setShowPasswordResetModal(true);
                                }}
                            >
                                {translate("OTM_RESET_PASSWORD")}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setShowAboutModal(true)}>{translate("OTM_ABOUT")}</Dropdown.Item>
                            <Dropdown.Item onClick={handleLogout}>{translate("OTM_LOGOUT")}</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
            <ChangeDomain show={showChangeDomainModal} cancelText="OTM_CANCEL" onCancel={() => setShowChangeDomainModal(false)} />
            <UserSettings show={showUserSettingsModal} onCancel={() => setShowUserSettingsModal(false)} id={user._id} />
            <ResetPassword
                show={showPasswordResetModal}
                onConfirm={() => {
                    setShowPasswordResetModal(false);
                }}
                onCancel={() => {
                    setShowPasswordResetModal(false);
                }}
            />
            <DeleteDomain show={showDeleteDomainModal} onCancel={() => setShowDeleteDomainModal(false)} />
            <AboutModal show={showAboutModal} onCancel={() => setShowAboutModal(false)} />
        </>
    );
};

export default Controls;
